/* Clients — the same bones as Contacts, with the columns a paying entity is
   about: revenue over time, jobs, contacts, open items.
   mockups/crm-v3-contacts-v1.html section #clients. Ported from the legacy
   CRM (2026-08-26), rebuilt here for CRM v3 slice 3.

   Money is buildClientJobIndex → src/apps/jobs/lib/revenue.js: a change order
   is part of its parent project, so it never appears as a second job or a
   second sum. The Open column is one query for the whole list (useOpenItems),
   never one per row and never the whole interactions collection.

   Not in this slice: the account radar strip (slice 8) and Save as segment
   (slice 4). */
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { C, inp, sel, btnS, btnP, btnD, badge } from "../../lib/styles.js";
import { fm, fmt, mkClientCode } from "../../lib/utils.js";
import {
  INDUSTRY_LABELS, LEAD_SOURCES, CLIENT_STATUSES, CLIENT_STATUS_COLORS, JOB_ROLES,
} from "../../lib/constants.js";
import ActivityTimeline from "./ActivityTimeline.jsx";
import { useInteractions } from "./useInteractions.js";
import { useOpenItems } from "./useOpenItems.js";
import { S, layout, cellStyle, ROW_CLASS } from "./listStyles.js";
import { Chips, SelectPill, TableFooter, ListRowStyle } from "./ListParts.jsx";
import {
  buildClientRows, filterClientRows, sortClientRows, paginate, barPct,
  CLIENT_STATUS_CHIPS, CLIENT_SORTS, INDUSTRY_FILTERS, JOB_WINDOWS,
  parseTags, commitIfStillActive, clientFiltersToRules, canSaveClientsAsSegment,
} from "./listModel.js";

const CSC = CLIENT_STATUS_COLORS(C);
const ACTIVITY_CAP = 6;
const arr = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/** "2026-05-17" → "May 17", or "Aug 2025" once it is out of the current year. */
function shortDate(iso, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return "";
  const [, y, mo, d] = m;
  const month = new Date(Date.UTC(Number(y), Number(mo) - 1, 1))
    .toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  return Number(y) === now.getFullYear() ? `${month} ${Number(d)}` : `${month} ${y}`;
}

export default function ClientsView({
  clients,
  contacts,
  jobs,
  savedQuotes,
  holds,
  role,
  saveClient,
  removeClient,
  openContactFrom,
  activeClient,
  setActiveClient,
  navigateBack,
  returnTo,
  isMobile,
  paletteOpen,
  onSaveAsSegment,
}) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("any");
  const [lastJobFilter, setLastJobFilter] = useState("any");
  const [sortKey, setSortKey] = useState("rev12");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const rowRefs = useRef({});

  const safeClients = useMemo(() => clients || [], [clients]);
  const safeContacts = useMemo(() => contacts || [], [contacts]);
  const safeJobs = useMemo(() => jobs || [], [jobs]);
  const safeQuotes = useMemo(() => savedQuotes || [], [savedQuotes]);
  const safeHolds = useMemo(() => holds || [], [holds]);

  /* The Open column: one query for the whole list, admin only. */
  const { records: openItems } = useOpenItems(role);

  const rows = useMemo(() => buildClientRows({
    clients: safeClients, contacts: safeContacts, jobs: safeJobs,
    quotes: safeQuotes, holds: safeHolds, openItems,
  }), [safeClients, safeContacts, safeJobs, safeQuotes, safeHolds, openItems]);

  const visible = useMemo(() => sortClientRows(
    filterClientRows(rows, { status: statusFilter, industry: industryFilter, lastJob: lastJobFilter }),
    sortKey,
  ), [rows, statusFilter, industryFilter, lastJobFilter, sortKey]);

  const filterKey = `${statusFilter}|${industryFilter}|${lastJobFilter}|${sortKey}`;
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) { setLastFilterKey(filterKey); setPage(1); }

  /* The bar is relative to the largest twelve-month figure in what is on
     screen after filtering, not in the whole book: filtering to one industry
     should re-scale the bars to that industry. */
  const maxRev = useMemo(() => visible.reduce((m, r) => Math.max(m, r.last12), 0), [visible]);
  const pageState = paginate(visible, page);

  const open = activeClient ? (safeClients.find(c => c.id === activeClient.id) || activeClient) : null;
  const openRow = open ? rows.find(r => r.id === open.id) : null;
  const openId = open?.id ?? null;

  /* Reset from the id, not from the click handlers: a client also becomes the
     open one through ⌘K, through the parent-studio link inside this drawer and
     through the back link, and a half-typed edit must not follow the operator
     onto the next account. Same rule as ContactsView. */
  const [lastOpenId, setLastOpenId] = useState(openId);
  if (openId !== lastOpenId) {
    setLastOpenId(openId);
    setEditing(false);
    setDraft(null);
    setShowAllActivity(false);
  }

  /* What "still on screen" means at the moment an in-flight save lands. */
  const activeIdRef = useRef(openId);
  useEffect(() => { activeIdRef.current = openId; }, [openId]);
  const commitSaved = useCallback(
    (record) => commitIfStillActive(activeIdRef.current, record, setActiveClient),
    [setActiveClient],
  );

  const closeDrawer = useCallback(() => {
    if (navigateBack) navigateBack(); else setActiveClient(null);
    if (openId) requestAnimationFrame(() => rowRefs.current[openId]?.focus?.());
  }, [openId, navigateBack, setActiveClient]);

  useEffect(() => {
    // The ⌘K palette owns Escape while it is up; see the note in ContactsView.
    if (!openId || paletteOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId, paletteOpen, closeDrawer]);

  const activity = useInteractions({ clientId: open ? open.id : null });
  const drawerOpen = Boolean(open);
  const now = new Date();

  const table = (
    <div>
      <ListRowStyle />
      <div style={S.box}>
        <div style={S.scroller}>
          <table style={S.table} data-testid="clients-table">
            <thead>
              <tr>
                <th style={S.th}>Client</th>
                {/* With the drawer open the table is 380px narrower. Industry
                    and Lifetime step aside — the same trade Contacts makes
                    with Tags — so Last job, Open and Status stay on screen.
                    Open is the column that costs money when it is ignored. */}
                {!drawerOpen && <th style={S.th}>Industry</th>}
                <th style={{ ...S.th, ...S.thRight }}>Contacts</th>
                <th style={{ ...S.th, ...S.thRight }}>Jobs</th>
                <th style={{ ...S.th, ...S.thRight }}>Revenue, 12 mo</th>
                {!drawerOpen && <th style={{ ...S.th, ...S.thRight }}>Lifetime</th>}
                <th style={S.th}>Last job</th>
                <th style={S.th}>Open</th>
                <th style={S.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {pageState.rows.map((r) => {
                const selected = open?.id === r.id;
                const cell = (base, first) => cellStyle(base, selected, first);
                return (
                  <tr
                    key={r.id}
                    ref={(el) => { if (el) rowRefs.current[r.id] = el; else delete rowRefs.current[r.id]; }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    className={ROW_CLASS}
                    data-testid={`client-row-${r.id}`}
                    style={{ cursor: "pointer", height: 44 }}
                    onClick={() => setActiveClient(r.record)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActiveClient(r.record);
                      }
                    }}
                  >
                    <td style={cell(null, true)}>
                      <div style={S.who}>
                        <div style={S.whoName}>
                          {r.name || "Unnamed"}
                          {r.clientCode && <span style={S.codePill}>{r.clientCode}</span>}
                        </div>
                        <div style={S.whoMeta}>
                          {r.subtitle}
                          {r.nda && <span style={S.small}>NDA</span>}
                        </div>
                      </div>
                    </td>
                    {!drawerOpen && (
                      <td style={cell()}>
                        {r.industry.length > 0 && (
                          <span style={S.indPill}>{INDUSTRY_LABELS[r.industry[0]] || r.industry[0]}</span>
                        )}
                        {r.industry.length > 1 && <span style={S.small}>+{r.industry.length - 1}</span>}
                      </td>
                    )}
                    <td style={cell({ ...S.tdRight, ...S.num })}>{r.contactCount}</td>
                    <td style={cell({ ...S.tdRight, ...S.num })}>{r.jobCount}</td>
                    <td style={cell({ ...S.mono, ...S.tdRight })}>
                      <span style={S.bar}><i style={{ ...S.barFill, width: `${barPct(r.last12, maxRev)}%` }} /></span>
                      {fmt(r.last12)}
                      {r.last12 === 0 && r.quotedTotal > 0 && (
                        <span style={S.small}>quoted {fmt(r.quotedTotal)}</span>
                      )}
                    </td>
                    {!drawerOpen && (
                      <td style={cell({ ...S.mono, ...S.tdRight })}>{fmt(r.lifetime)}</td>
                    )}
                    <td style={cell(S.touch)}>
                      {r.lastJob
                        ? <><span style={S.touchNow}>{shortDate(r.lastJob, now)}</span>{r.lastJobHeld ? " held" : ""}</>
                        : "—"}
                    </td>
                    <td style={cell()}>
                      {r.openItem && (
                        <span style={S.openItem} title={r.openItem.summary || ""}>
                          <i style={S.openDot} />{r.openItemLabel}
                        </span>
                      )}
                    </td>
                    <td style={cell()}>
                      <span style={badge(CSC[r.status] || C.muted)}>
                        {CLIENT_STATUSES[r.status] || r.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pageState.total === 0 && (
          <div style={S.empty}>
            {safeClients.length === 0 ? "No clients yet." : "No clients match these filters."}
          </div>
        )}
        <TableFooter
          page={pageState}
          noun="client"
          sortLabel={CLIENT_SORTS.find(s => s.key === sortKey)?.label}
          onPage={setPage}
        />
      </div>
    </div>
  );

  return (
    <div>
      <div style={S.filters} data-testid="clients-filters">
        <Chips options={CLIENT_STATUS_CHIPS} value={statusFilter} onChange={setStatusFilter} testid="clients-status" />
        <span style={S.gap} />
        <SelectPill label="Industry" value={industryFilter} options={INDUSTRY_FILTERS}
          onChange={setIndustryFilter} testid="clients-industry-filter" />
        <SelectPill label="Last job" value={lastJobFilter} options={JOB_WINDOWS}
          onChange={setLastJobFilter} testid="clients-lastjob-filter" />
        <SelectPill label="Sort" value={sortKey} options={CLIENT_SORTS}
          onChange={setSortKey} testid="clients-sort" />
        {onSaveAsSegment && (
          <button
            type="button"
            style={{
              ...btnS, padding: "5px 11px", fontSize: 11,
              ...(canSaveClientsAsSegment(visible) ? null : { opacity: 0.45, cursor: "not-allowed" }),
            }}
            data-testid="clients-save-as-segment"
            /* Zero rows means "nobody", and this conversion is built from the
               visible rows. Saving it would produce a segment with no client
               rule at all — every bulk-eligible contact in the book, from a
               filter that matched nothing. See canSaveClientsAsSegment. */
            disabled={!canSaveClientsAsSegment(visible)}
            title={canSaveClientsAsSegment(visible)
              ? "Everyone at these accounts, as a saved audience"
              : "No accounts match these filters, so there is no audience to save"}
            onClick={() => onSaveAsSegment(clientFiltersToRules(visible))}
          >
            Save as segment
          </button>
        )}
        <span style={S.count} data-testid="clients-count">
          {visible.length.toLocaleString()} client{visible.length === 1 ? "" : "s"}
        </span>
      </div>

      <div style={layout(drawerOpen, isMobile)} data-testid="clients-layout">
        {(!drawerOpen || !isMobile) && table}
        {drawerOpen && (
          <ClientDrawer
            row={openRow}
            client={open}
            clients={safeClients}
            activity={activity}
            showAllActivity={showAllActivity}
            setShowAllActivity={setShowAllActivity}
            editing={editing}
            setEditing={setEditing}
            draft={draft}
            setDraft={setDraft}
            saveClient={saveClient}
            removeClient={removeClient}
            setActiveClient={setActiveClient}
            commitSaved={commitSaved}
            openContactFrom={openContactFrom}
            onClose={closeDrawer}
            returnTo={returnTo}
            isMobile={isMobile}
            openItems={openItems}
          />
        )}
      </div>
    </div>
  );
}

/* ═══ the drawer ═══════════════════════════════════════════════════════════ */

function ClientDrawer({
  row, client: cl, clients, activity, showAllActivity, setShowAllActivity,
  editing, setEditing, draft, setDraft, saveClient, removeClient,
  setActiveClient, commitSaved, openContactFrom, onClose, returnTo, isMobile, openItems,
}) {
  const d = draft || cl;
  const set = (field, val) => setDraft(prev => ({ ...(prev || cl), [field]: val }));
  const eInp = { ...inp, fontSize: 12, padding: "5px 8px" };
  const eSel = { ...sel, fontSize: 12, padding: "5px 8px" };

  const linked = row?.linkedContacts || [];
  const parent = row?.parent || null;
  const records = activity.records || [];
  const shown = showAllActivity ? records : records.slice(0, ACTIVITY_CAP);
  const openN = (openItems || []).filter(
    r => String(r?.clientId || "").toLowerCase() === String(cl.id || "").toLowerCase()
      && (r.status || "open") === "open").length;

  const leadSourceKey = Object.keys(LEAD_SOURCES).includes(d.leadSource)
    ? d.leadSource : d.leadSource ? "_freeform" : "";

  const save = async () => {
    const toSave = { ...d };
    delete toSave._leadSourceOther;
    // Raw text until here, the same as the Contacts drawer. See parseTags.
    toSave.tags = parseTags(toSave._tagsText ?? arr(toSave.tags).join(", "));
    delete toSave._tagsText;
    toSave.social = { ...(cl.social || {}), ...(toSave.social || {}) };
    if (!toSave.clientCode) toSave.clientCode = mkClientCode(toSave.name);
    try {
      await saveClient(toSave);
      // Only the record that was being saved closes its editor: if the user has
      // moved on and started editing another record, that draft is theirs.
      // commitSaved() says whether the saved record is still the open one.
      if (commitSaved(toSave)) { setEditing(false); setDraft(null); }
    } catch (err) {
      window.alert("Save failed: " + err.message);
    }
  };

  return (
    <div style={isMobile ? S.drawerMobile : S.drawer} data-testid="client-drawer">
      <div style={S.dhead}>
        <div style={S.dback}>
          <button type="button" style={S.dbackBtn} onClick={onClose} data-testid="client-drawer-back">
            ← {returnTo === "contacts" ? "Contacts" : "Clients"}
          </button>
          <span style={{ opacity: 0.8 }}>esc</span>
        </div>
        <div style={S.dname}>
          {cl.name || "Unnamed"}
          {cl.clientCode && <span style={S.codePill}>{cl.clientCode}</span>}
        </div>
        <div style={S.dco}>{cl.orgDomain || parent?.name || ""}</div>
        <div style={S.dchips}>
          <span style={badge(CSC[cl.status] || C.muted)}>
            {CLIENT_STATUSES[cl.status] || cl.status || "Active"}
          </span>
          {cl.noModelSessions && <span style={badge(C.danger)} data-testid="client-drawer-nda">NDA</span>}
          {openN > 0 && (
            <span style={{
              ...badge(C.danger), textTransform: "none", letterSpacing: "0.02em",
              fontSize: 11, padding: "3px 9px", borderRadius: 999,
            }}>{openN} open</span>
          )}
        </div>
      </div>

      {/* ── Details ── */}
      <div style={S.dsec}>
        <div style={S.dsecTitle}>
          <span>Details</span>
          <button
            type="button"
            style={S.dsecAction}
            data-testid="client-drawer-edit"
            onClick={() => {
              if (editing) { setEditing(false); setDraft(null); }
              else { setEditing(true); setDraft({ ...cl, _tagsText: arr(cl.tags).join(", ") }); }
            }}
          >
            {editing ? "Cancel" : "Edit"}
          </button>
        </div>

        {editing ? (
          <div style={{ display: "grid", gap: 8, fontSize: 12 }}>
            <label>
              <div style={S.kvK}>Client code</div>
              <input style={{ ...eInp, fontFamily: fm, maxWidth: 140 }} maxLength={6} value={d.clientCode || ""}
                placeholder={mkClientCode(d.name)}
                onChange={ev => set("clientCode", ev.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))} />
            </label>
            <label>
              <div style={S.kvK}>Billing address</div>
              <textarea style={{ ...eInp, minHeight: 44, resize: "vertical", width: "100%" }}
                value={d.billingAddress || ""} onChange={ev => set("billingAddress", ev.target.value)} />
            </label>
            <label>
              <div style={S.kvK}>Email</div>
              <input style={eInp} value={d.email || ""} onChange={ev => set("email", ev.target.value)} />
            </label>
            <label>
              <div style={S.kvK}>Phone</div>
              <input style={eInp} value={d.phone || ""} onChange={ev => set("phone", ev.target.value)} />
            </label>
            <label>
              <div style={S.kvK}>Domain</div>
              <input style={eInp} value={d.orgDomain || ""} placeholder="example.com"
                onChange={ev => set("orgDomain", ev.target.value)} />
            </label>
            <label>
              <div style={S.kvK}>Status</div>
              <select style={eSel} value={d.status || "active"} onChange={ev => set("status", ev.target.value)}>
                {Object.entries(CLIENT_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <div>
              <div style={S.kvK}>Industry</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {Object.entries(INDUSTRY_LABELS).map(([k, v]) => {
                  const checked = arr(d.industry).includes(k);
                  return (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, cursor: "pointer" }}>
                      <input type="checkbox" checked={checked} style={{ accentColor: C.teal }}
                        onChange={() => set("industry", checked
                          ? arr(d.industry).filter(x => x !== k)
                          : [...arr(d.industry), k])} />
                      {v}
                    </label>
                  );
                })}
              </div>
            </div>
            <label>
              <div style={S.kvK}>Source</div>
              <select style={eSel} value={leadSourceKey} onChange={ev => {
                const val = ev.target.value;
                if (val === "_freeform") { set("leadSource", d._leadSourceOther || ""); }
                else { set("leadSource", val); set("_leadSourceOther", undefined); }
              }}>
                <option value="">—</option>
                {Object.entries(LEAD_SOURCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                <option value="_freeform">Other (specify)</option>
              </select>
              {leadSourceKey === "_freeform" && (
                <input style={{ ...eInp, marginTop: 4 }} value={d.leadSource || ""}
                  onChange={ev => set("leadSource", ev.target.value)} placeholder="Specify…" />
              )}
            </label>
            <label>
              <div style={S.kvK}>Referred by</div>
              <input style={eInp} value={d.referredBy || ""} onChange={ev => set("referredBy", ev.target.value)} />
            </label>
            <label>
              <div style={S.kvK}>Parent studio</div>
              <select style={eSel} value={d.parentClientId || ""}
                onChange={ev => set("parentClientId", ev.target.value || null)}>
                <option value="">— None</option>
                {clients.filter(c => c.id !== cl.id)
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map(c => <option key={c.id} value={c.id}>{c.name}{c.clientCode ? ` (${c.clientCode})` : ""}</option>)}
              </select>
            </label>
            <label>
              <div style={S.kvK}>Tags</div>
              <input style={eInp} data-testid="client-drawer-tags" value={d._tagsText ?? arr(d.tags).join(", ")}
                placeholder="comma separated"
                onChange={ev => set("_tagsText", ev.target.value)} />
            </label>
            <label>
              <div style={S.kvK}>LinkedIn</div>
              <input style={eInp} value={d.social?.linkedin || ""}
                onChange={ev => set("social", { ...(d.social || {}), linkedin: ev.target.value })} />
            </label>
            <label>
              <div style={S.kvK}>Website</div>
              <input style={eInp} value={d.social?.website || ""}
                onChange={ev => set("social", { ...(d.social || {}), website: ev.target.value })} />
            </label>
            {/* The flag is a policy control our own tools honor, not a technical
                boundary: Firestore rules know nothing about it. */}
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                data-testid="client-drawer-nda-toggle"
                checked={d.noModelSessions === true}
                style={{ accentColor: C.danger }}
                onChange={ev => set("noModelSessions", ev.target.checked)}
              />
              NDA: keep out of model sessions
            </label>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button style={{ ...btnP, padding: "5px 14px", fontSize: 11 }} onClick={save} data-testid="client-drawer-save">Save</button>
              <button style={{ ...btnS, padding: "5px 14px", fontSize: 11 }}
                onClick={() => { setEditing(false); setDraft(null); }}>Cancel</button>
              <button
                style={{ ...btnD, padding: "5px 12px", fontSize: 11, marginLeft: "auto" }}
                onClick={() => {
                  if (!window.confirm(`Permanently delete "${cl.name}"? This cannot be undone.`)) return;
                  removeClient(cl.id);
                  onClose();
                }}
              >Delete</button>
            </div>
          </div>
        ) : (
          <div style={S.kv}>
            <span style={S.kvK}>Billing</span>
            <span style={{ ...S.kvV, whiteSpace: "pre-wrap" }}>{cl.billingAddress || "—"}</span>
            <span style={S.kvK}>Email</span>
            <span style={S.kvV}>{cl.email || "—"}</span>
            <span style={S.kvK}>Phone</span>
            <span style={{ ...S.kvV, ...S.kvVMono }}>{cl.phone || "—"}</span>
            <span style={S.kvK}>Industry</span>
            <span style={S.kvV}>{arr(cl.industry).map(k => INDUSTRY_LABELS[k] || k).join(", ") || "—"}</span>
            <span style={S.kvK}>Source</span>
            <span style={S.kvV}>{LEAD_SOURCES[cl.leadSource] || cl.leadSource || "—"}</span>
            <span style={S.kvK}>Referred by</span>
            <span style={S.kvV}>{cl.referredBy || "—"}</span>
            <span style={S.kvK}>Parent studio</span>
            <span style={S.kvV}>
              {parent
                ? <button type="button" style={S.dcoLink} onClick={() => setActiveClient(parent)}>{parent.name}</button>
                : "—"}
            </span>
            <span style={S.kvK}>Tags</span>
            <span style={S.kvV} data-testid="client-drawer-tag-list">
              {arr(cl.tags).length ? arr(cl.tags).map(t => <span key={t} style={S.tag}>{t}</span>) : "—"}
            </span>
            <span style={S.kvK}>LinkedIn</span>
            <span style={S.kvV}>{cl.social?.linkedin || "—"}</span>
          </div>
        )}
      </div>

      {/* ── Revenue ── */}
      <div style={S.dsec}>
        <div style={S.dsecTitle}><span>Revenue</span></div>
        <div style={S.tiles} data-testid="client-drawer-revenue">
          <div style={S.tile}>
            <div style={S.tileN}>{fmt(row?.last12 || 0)}</div>
            <div style={S.tileL}>12 months</div>
          </div>
          <div style={S.tile}>
            <div style={S.tileN}>{fmt(row?.lifetime || 0)}</div>
            <div style={S.tileL}>lifetime</div>
          </div>
          <div style={S.tile}>
            <div style={S.tileN}>{row?.jobCount || 0}</div>
            <div style={S.tileL}>{(row?.jobCount || 0) === 1 ? "job" : "jobs"}</div>
          </div>
        </div>
      </div>

      {/* ── Contacts ── */}
      <div style={S.dsec}>
        <div style={S.dsecTitle}><span>Contacts</span><span>{linked.length}</span></div>
        {linked.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Nobody linked yet.</div>}
        {linked.map(({ link, contact }) => (
          <div
            key={contact.id}
            role="button"
            tabIndex={0}
            data-testid={`client-drawer-contact-${contact.id}`}
            style={{
              padding: "6px 0", borderBottom: `1px solid ${C.border}`, cursor: "pointer",
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
            }}
            onClick={() => openContactFrom(contact, "clients")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openContactFrom(contact, "clients"); }
            }}
          >
            <span style={{ fontSize: 12.5 }}>{contact.name}</span>
            <span style={{ ...badge(C.muted), fontSize: 8 }}>
              {JOB_ROLES[link.role] || JOB_ROLES[link.linkType] || link.role || link.linkType || "Contact"}
            </span>
          </div>
        ))}
      </div>

      {/* ── Activity ── */}
      <div style={S.dsecLast}>
        <ActivityTimeline
          variant="client"
          records={shown}
          loading={activity.loading}
          error={activity.error}
        />
        {records.length > ACTIVITY_CAP && (
          <button
            type="button"
            style={S.moreBtn}
            data-testid="client-drawer-activity-toggle"
            onClick={() => setShowAllActivity(v => !v)}
          >
            {showAllActivity ? `Newest ${ACTIVITY_CAP}` : `All ${records.length}`}
          </button>
        )}
      </div>
    </div>
  );
}
